import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { getPracticeOverviewTrend } from 'api/group';
import {
    TPracticeOverviewTrendVo,
    TPracticeOverviewSummaryVo,
    TPracticeOverviewRange,
} from '../../types';
import RangeDropdown from '../RangeDropdown';
import s from './s.module.less';

type Props = {
    summary: TPracticeOverviewSummaryVo | null;
};

type TrendPoint = { x: number; y: number; val: number; label: string };

const formatDateLabel = (dateStr: string) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const TrendChart: React.FC<{ data: { date: string; value: number | null }[] }> = ({ data }) => {
    const chartWidth = 1200;
    const chartHeight = 400;
    const pad = { t: 24, r: 24, b: 36, l: 56 };
    const cw = chartWidth - pad.l - pad.r;
    const ch = chartHeight - pad.t - pad.b;

    const [hoverIdx, setHoverIdx] = useState<number | null>(null);
    const svgRef = useRef<SVGSVGElement>(null);

    const validData = data.filter((d) => d.value != null) as { date: string; value: number }[];
    if (validData.length === 0) return <div className={s.empty}>No data available</div>;

    const max = useMemo(() => {
        const rawMax = Math.max(...validData.map((d) => d.value));
        return rawMax > 0 ? rawMax * 1.15 : 100;
    }, [validData]);

    const points: TrendPoint[] = useMemo(
        () =>
            validData.map((d, i) => ({
                x: pad.l + (validData.length === 1 ? cw / 2 : (i / (validData.length - 1)) * cw),
                y: pad.t + ch - (d.value / max) * ch,
                val: d.value,
                label: formatDateLabel(d.date),
            })),
        [validData, max, cw, ch],
    );

    const buildBezierPath = useCallback((pts: TrendPoint[]) => {
        if (pts.length < 2) return `M ${pts[0].x} ${pts[0].y}`;
        let d = `M ${pts[0].x} ${pts[0].y}`;
        for (let i = 1; i < pts.length; i++) {
            const cx1 = (pts[i - 1].x + pts[i].x) / 2;
            const cy1 = pts[i - 1].y;
            const cx2 = (pts[i - 1].x + pts[i].x) / 2;
            const cy2 = pts[i].y;
            d += ` C ${cx1} ${cy1}, ${cx2} ${cy2}, ${pts[i].x} ${pts[i].y}`;
        }
        return d;
    }, []);

    const linePath = useMemo(() => buildBezierPath(points), [points, buildBezierPath]);
    const areaPath = useMemo(() => {
        const base = buildBezierPath(points);
        return `${base} L ${points[points.length - 1].x} ${pad.t + ch} L ${points[0].x} ${pad.t + ch} Z`;
    }, [points, buildBezierPath, ch]);

    const ticks = useMemo(() => {
        const arr: number[] = [];
        for (let g = 0; g <= 4; g++) {
            arr.push(Math.round(max - (max * g) / 4));
        }
        return arr;
    }, [max]);

    const formatLabel = (val: number) => {
        if (val >= 1000) return `$${(val / 1000).toFixed(val % 1000 === 0 ? 0 : 1)}k`;
        return `$${val}`;
    };

    const xLabelStep = Math.max(1, Math.floor(validData.length / 8));

    const handleMouseMove = useCallback(
        (e: React.MouseEvent<SVGSVGElement>) => {
            const svg = svgRef.current;
            if (!svg) return;
            const rect = svg.getBoundingClientRect();
            const scaleX = chartWidth / rect.width;
            const mx = (e.clientX - rect.left) * scaleX;
            let closest = -1;
            let minD = Infinity;
            points.forEach((p, i) => {
                const d = Math.abs(p.x - mx);
                if (d < minD) {
                    minD = d;
                    closest = i;
                }
            });
            setHoverIdx(minD < 80 ? closest : null);
        },
        [points],
    );

    const hoverPoint = hoverIdx != null ? points[hoverIdx] : null;

    return (
        <div className={s.chartWrap}>
            <svg
                ref={svgRef}
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                className={s.chartSvg}
                preserveAspectRatio="xMidYMid meet"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setHoverIdx(null)}
            >
                <defs>
                    <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0E62F4" stopOpacity="0.12" />
                        <stop offset="100%" stopColor="#0E62F4" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {ticks.map((tick, g) => {
                    const y = pad.t + (g / 4) * ch;
                    return (
                        <g key={g}>
                            <line x1={pad.l} y1={y} x2={chartWidth - pad.r} y2={y} className={s.gridLine} />
                            <text x={pad.l - 8} y={y + 4} textAnchor="end" className={s.axisLabel}>
                                {formatLabel(tick)}
                            </text>
                        </g>
                    );
                })}
                <path d={areaPath} fill="url(#trendGrad)" />
                <path d={linePath} className={s.dataLine} />
                {points.map((p, i) => (
                    <circle key={i} cx={p.x} cy={p.y} r={3} className={s.dataDot} />
                ))}
                {validData.map((d, i) => {
                    if (i % xLabelStep !== 0 && i !== validData.length - 1) return null;
                    return (
                        <text key={i} x={points[i].x} y={chartHeight - 8} textAnchor="middle" className={s.axisLabel}>
                            {formatDateLabel(d.date)}
                        </text>
                    );
                })}
                {hoverPoint && (
                    <>
                        <line
                            x1={hoverPoint.x}
                            y1={pad.t}
                            x2={hoverPoint.x}
                            y2={pad.t + ch}
                            className={s.hoverLine}
                        />
                        <circle cx={hoverPoint.x} cy={hoverPoint.y} r={5} fill="#0E62F4" />
                        <circle cx={hoverPoint.x} cy={hoverPoint.y} r={2} fill="#fff" />
                    </>
                )}
            </svg>
            {hoverPoint && (
                <div
                    className={s.tooltip}
                    style={{
                        left: `${(hoverPoint.x / chartWidth) * 100}%`,
                        top: `${(hoverPoint.y / chartHeight) * 100}%`,
                    }}
                >
                    <div className={s.ttLabel}>{hoverPoint.label}</div>
                    <div className={s.ttVal}>${hoverPoint.val.toLocaleString()}</div>
                </div>
            )}
        </div>
    );
};

const formatCents = (cents: number): string => {
    const dollars = cents / 100;
    if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}k`;
    return `$${dollars.toLocaleString()}`;
};

const DEFAULT_RANGE: TPracticeOverviewRange = 'LAST_30D';

const FinancialPerformance: React.FC<Props> = ({ summary }) => {
    const [range, setRange] = useState<TPracticeOverviewRange>(DEFAULT_RANGE);
    const [trend, setTrend] = useState<TPracticeOverviewTrendVo | null>(null);

    useEffect(() => {
        getPracticeOverviewTrend({ range, metric: 'PAYOUT' }).then((res) => {
            if (!res.error) setTrend(res?.data?.data ?? null);
        });
    }, [range]);

    const isUp = (summary?.payoutChangePct ?? 0) >= 0;

    return (
        <div>
            <div className={s.header}>
                <h2 className={s.title}>Financial Performance</h2>
                <RangeDropdown
                    value={range}
                    onChange={(v) => setRange(v as TPracticeOverviewRange)}
                />
            </div>
            <div className={s.card}>
                {summary && (
                    <div className={s.trendSummary}>
                        <div className={s.trendValRow}>
                            <span className={s.trendVal}>{formatCents(summary.payoutCents)}</span>
                            <span className={s.trendFrom}>from {formatCents(summary.payoutCentsPrior)}</span>
                        </div>
                        <div className={s.trendLine}>
                            <span className={`${s.trendBadge} ${isUp ? s.trendUp : s.trendDown}`}>
                                <svg viewBox="0 0 20 20" fill="none" stroke={isUp ? '#065F46' : '#991B1B'} strokeWidth="2" strokeLinecap="round" width="20" height="20">
                                    {isUp ? (
                                        <>
                                            <line x1="10" y1="14" x2="10" y2="6" />
                                            <polyline points="7 9 10 6 13 9" />
                                        </>
                                    ) : (
                                        <>
                                            <line x1="10" y1="6" x2="10" y2="14" />
                                            <polyline points="7 11 10 14 13 11" />
                                        </>
                                    )}
                                </svg>
                                <span className={s.trendPct} style={{ color: isUp ? '#065F46' : '#991B1B' }}>
                                    {Math.abs(summary.payoutChangePct).toFixed(1)}%
                                </span>
                            </span>
                            <span className={s.trendPeriod}>from last period</span>
                        </div>
                    </div>
                )}
                {trend?.series && <TrendChart data={trend.series} />}
            </div>
        </div>
    );
};

export default FinancialPerformance;
